import React, { Component } from 'react';
import qs from 'qs';
import { connect } from 'react-redux';
import { Prompt, withRouter } from 'react-router';
import PropTypes from 'prop-types';
import noop from 'lodash/noop';
import get from 'lodash/get';
import Logger from 'observability/Logger';
import { notifyError as notifyErrorAction } from 'common/compounds/Notification/modules';
import { classNames } from 'utils/classNames';
import { getEventPageUrlFromEventDomain, getOriginWithoutAppSubdomain } from 'utils/resolveUrl';
import IFrame from 'common/components/IFrame';
import {
    TOUCHPOINT_TYPE_CARD,
    TOUCHPOINT_TYPE_CONFIRMATION,
    TOUCHPOINT_TYPE_PAGE,
    TOUCHPOINT_TYPE_SESSION,
    TOUCHPOINT_TYPE_BLOCK,
} from 'common/constants/touchpointTypes';
import waitForChildIframe from './utils/waitForChildIframe';

import {
    bindEventListenerForOpenModals,
    overwriteSplashUiForDisplayingModalOverlays,
} from './utils/iframeModalHandlers';

import {
    PUBSUB_SAVE_SUCCESS,
    PUBSUB_PAGE_CHANGES_MADE,
    PUBSUB_CONTEXT,
    PUBSUB_NAVIGATE_TO_EVENT_SETTINGS,
} from './constants';

import './styles.styl';

const LOG = new Logger('CmsIframe');
const BASE_CLASS_NAME = 'CmsIframe';
const TOUCHPOINT_PARAMS = [
    TOUCHPOINT_TYPE_CARD,
    TOUCHPOINT_TYPE_CONFIRMATION,
    TOUCHPOINT_TYPE_PAGE,
    TOUCHPOINT_TYPE_SESSION,
    TOUCHPOINT_TYPE_BLOCK,
];

class CmsIframe extends Component {
    static propTypes = {
        eventDomain: PropTypes.string,
        csrfToken: PropTypes.string.isRequired,
        onPageLoad: PropTypes.func,
        className: PropTypes.string,
        onModalOpen: PropTypes.func,
        onModalClose: PropTypes.func,
        queryParams: PropTypes.objectOf(PropTypes.string),
        iframeName: PropTypes.string,
        history: PropTypes.object.isRequired,
        // This is a query param, but it's separated so we can specify a default
        touchpointParam: PropTypes.oneOf(TOUCHPOINT_PARAMS),

        // If you want the benefits of styling a workspace around
        // this component, use the mixins in the style file
        hasWorkspaceStyling: PropTypes.bool,

        // Some workspace topbars have tabs which toggle the CMS
        // sidebar and go the full width of the screen, others do not.
        // It affects whether we need a top margin on the splash-container
        // within the iframe.
        hasSidebarTabs: PropTypes.bool,

        // Pass custom PubSub events to take action from things that happen inside the CMS
        // Use this to avoid adding code in the component that couples CmsIframe to a use case
        customPubSubListeners: PropTypes.arrayOf(
            PropTypes.shape({
                eventName: PropTypes.string.isRequired,
                handler: PropTypes.func.isRequired,
            }),
        ),

        notifyError: PropTypes.func.isRequired,
    };

    static defaultProps = {
        eventDomain: undefined,
        className: '',
        hasWorkspaceStyling: false,
        hasSidebarTabs: false,
        onPageLoad: noop,
        onModalOpen: noop,
        onModalClose: noop,
        queryParams: {},
        iframeName: 'CmsPage',
        touchpointParam: TOUCHPOINT_TYPE_PAGE,
        customPubSubListeners: [],
    };

    state = {
        hasUnsavedChanges: false,
    };

    componentWillUnmount() {
        if (this.modalObserver) {
            this.modalObserver.disconnect();
        }

        this.unsubscribeFromUnsavedChangesState();
    }

    setIframeRef = (iframeComponent) => {
        if (iframeComponent) {
            this.iframe = iframeComponent.iframe;
        }
    };

    handleInnerPageIframeLoad = () => {
        if (this.iframe && this.iframe.contentDocument) {
            this.iframeWindow = this.iframe.contentDocument.defaultView;
        }

        if (!this.iframe || !this.iframeWindow) {
            throw new Error(`${BASE_CLASS_NAME}: Iframe reference not set`);
        }

        if (this.props.hasWorkspaceStyling) {
            this.modalObserver = bindEventListenerForOpenModals(this.iframeWindow, this.handleCmsModalOpen);

            overwriteSplashUiForDisplayingModalOverlays(this.iframeWindow, this.handleCmsModalOpen);
        }

        if (this.props.hasSidebarTabs) {
            // TODO Change this direct DOM manipulation by a message based manipulation
            this.iframeWindow.document.querySelector('body').className += ' with-sidebar-tabs';
        }

        try {
            waitForChildIframe(this.iframeWindow, () => {
                this.subscribeToUnsavedChangesState();
                this.props.onPageLoad(this.iframeWindow);
            });
        } catch (e) {
            LOG.error('Error on handleInnerPageIframeLoad', e);
            this.props.notifyError("There's been an error");
        }
    };

    /**
     * Subscribe to changes in the child iframe, so that we can indicate
     * the correct thing on our Save button and prompt the user about unsaved
     * changes.
     */
    subscribeToUnsavedChangesState = () => {
        // TODO Change this subscription by a message subscription to the iframe
        const PubSub = get(this.iframeWindow, 'splash.PubSub');

        if (!PubSub) {
            return;
        }

        PubSub.subscribe(PUBSUB_NAVIGATE_TO_EVENT_SETTINGS, {
            context: PUBSUB_CONTEXT,
            eventHandler: () => this.props.history.push('/settings'),
        });
        PubSub.subscribe(PUBSUB_PAGE_CHANGES_MADE, {
            context: PUBSUB_CONTEXT,
            eventHandler: () => this.setState({ hasUnsavedChanges: true }),
        });
        PubSub.subscribe(PUBSUB_SAVE_SUCCESS, {
            context: PUBSUB_CONTEXT,
            eventHandler: () => this.setState({ hasUnsavedChanges: false }),
        });

        this.props.customPubSubListeners.forEach(({ eventName, handler }) => {
            PubSub.subscribe(eventName, {
                context: PUBSUB_CONTEXT,
                eventHandler: handler,
            });
        });
    };

    /**
     * When the component unmounts, we want to remove the event listeners we
     * set up. Sometimes the iframe's contentWindow won't be available, or the
     * document js context will have changed in a way that would result in a
     * cross-frame access error, so we wrap the whole thing in a try/catch.
     */
    unsubscribeFromUnsavedChangesState() {
        // TODO Change the way of unsubscribing to a message based approach
        try {
            const { PubSub } = this.iframeWindow.splash;

            PubSub.unsubscribe(PUBSUB_CONTEXT, PUBSUB_NAVIGATE_TO_EVENT_SETTINGS);
            PubSub.unsubscribe(PUBSUB_CONTEXT, PUBSUB_PAGE_CHANGES_MADE);
            PubSub.unsubscribe(PUBSUB_CONTEXT, PUBSUB_SAVE_SUCCESS);

            this.props.customPubSubListeners.forEach(({ eventName }) => {
                PubSub.unsubscribe(PUBSUB_CONTEXT, eventName);
            });
        } catch (e) {
            if (__DEVELOPMENT__) {
                LOG.warn('Exception unsubscribing from CmsIframe events', e);
            }
        }
    }

    handleCmsModalOpen = (isOpen) => {
        if (isOpen) {
            this.props.onModalOpen();
        } else {
            this.props.onModalClose();
        }
    };

    /**
     * Returns the URL to load the CMS iframe
     *
     * @return {string}
     */
    getIframeUrl() {
        const { csrfToken, queryParams, touchpointParam } = this.props;

        const params = {
            touchpointType: touchpointParam,
            ...queryParams,
            // Only add csrfToken if it's not empty (not undefined, null, or '')
            ...(csrfToken ? { csrfToken } : {}),
        };

        const urlsByTouchpointType = {
            [TOUCHPOINT_TYPE_BLOCK]: () => {
                const siteDomain = getOriginWithoutAppSubdomain();
                if (!params.blockId) {
                    LOG.warn('[CmsIframe] no block ID passed');
                }
                return `${siteDomain}/cms-tools?${qs.stringify(params)}`;
            },
            default: () => getEventPageUrlFromEventDomain(this.props.eventDomain, params),
        };

        return (urlsByTouchpointType[touchpointParam] || urlsByTouchpointType.default)();
    }

    render() {
        const baseClassName = BASE_CLASS_NAME;
        const { className, iframeName } = this.props;
        const iframeUrl = this.getIframeUrl();

        const baseClassNames = classNames({
            [baseClassName]: true,
            [className]: className,
        });

        return (
            <div className={`${baseClassName}__iframe-container`}>
                <Prompt
                    when={this.state.hasUnsavedChanges}
                    message={() => 'You have unsaved changes. Do you wish to continue?'}
                />
                <IFrame
                    className={baseClassNames}
                    url={iframeUrl}
                    name={iframeName}
                    ref={this.setIframeRef}
                    onIframeLoaded={this.handleInnerPageIframeLoad}
                    allowTransparency
                />
            </div>
        );
    }
}

const mapDispatchToProps = {
    notifyError: notifyErrorAction,
};

export default withRouter(connect(null, mapDispatchToProps)(CmsIframe));
